import { Box, css, styled } from "@mui/material";
import { glMatrix } from "gl-matrix";
import { lazy, MutableRefObject, RefCallback, Suspense, useCallback, useEffect, useRef, useState } from "react";

import { useModelRevisions } from "apis/procore/modelRevisions";
import { useAppSelector } from "app/redux-store-interactions";
import { LinearProgress, Loading } from "components";
import { explorerGlobalsActions, useExplorerGlobals } from "contexts/explorerGlobals";
import { useLoadAlignments } from "features/alignment/hooks/useLoadAlignments";
import { useHandleClipping } from "features/clippingPlanes";
import { useHandleDeviations } from "features/deviations";
import { useHandleDitioAuth } from "features/ditio";
import { Engine2D } from "features/engine2D/engine2D";
import { Engine2DHtmlInteractions } from "features/engine2D/engine2DHtmlInteractions";
import { Engine2DInteractions } from "features/engine2D/engine2DInteractions";
import { useRenderLocationFormAssets } from "features/forms/hooks/useRenderLocationFormAssets";
import { useHandleImages } from "features/images";
import { useHandleJiraKeepAlive } from "features/jira";
import { useHandleManhole } from "features/manhole";
import { useHandleLocationMarker } from "features/myLocation";
import { useHandleOffline } from "features/offline";
import { useHandleCrossSection } from "features/orthoCam";
import { useHandleOutlineLasers } from "features/outlineLaser";
import { PerformanceStats, RenderPerfSampler } from "features/performanceStats";
import { useHandleUrlSearch } from "features/search";
import { SpaceMouseCommands, useSpaceMouse } from "features/spaceMouse";
import { useHandleXsiteManageKeepAlive, useHandleXsiteManageMachineLocations } from "features/xsiteManage";
import { useGlobalShortcuts } from "hooks/useGlobalShortcuts";
import { useIntercom } from "hooks/useIntercom";
import { useRedirectWheelEvents } from "hooks/useRedirectWheelEvents";
import { useRefreshProjectPermissions } from "hooks/useRefreshProjectPermissions";
import { selectIsProcoreDesign } from "slices/explorer";
import { UxFlavor } from "slices/explorer/types";
import { AsyncStatus } from "types/misc";

import { ELEMENT_IDS } from "../bookmarks/utils";
import { useAutofreeze } from "./hooks/useAutofreeze";
import { useCanvasClickHandler } from "./hooks/useCanvasClickHandler";
import { useCanvasEventHandlers } from "./hooks/useCanvasEventHandlers";
import { useCrashTelemetry } from "./hooks/useCrashTelemetry";
import { useHandleAdvancedSettings } from "./hooks/useHandleAdvancedSettings";
import { useHandleBackground } from "./hooks/useHandleBackground";
import { useHandleCameraMoved } from "./hooks/useHandleCameraMoved";
import { useHandleCameraSpeed } from "./hooks/useHandleCameraSpeed";
import { useHandleCameraState } from "./hooks/useHandleCameraState";
import { useHandleCanvasCursor } from "./hooks/useHandleCanvasCursor";
import { useHandleClippingOutlines } from "./hooks/useHandleClippingOutlines";
import { useHandleGrid } from "./hooks/useHandleGrid";
import { useHandleHeadlight } from "./hooks/useHandleHeadlight";
import { useHandleHighlights } from "./hooks/useHandleHighlights";
import { useHandleInit } from "./hooks/useHandleInit";
import { useHandleInitialBookmark } from "./hooks/useHandleInitialBookmark";
import { useHandlePointVisualization } from "./hooks/useHandlePointVisualization";
import { useHandleSubtrees } from "./hooks/useHandleSubtrees";
import { useHandleTerrain } from "./hooks/useHandleTerrain";
import { useNotifyVisibilityChanged } from "./hooks/useNotifyVisibilityChanged";
import { usePreloadBrep } from "./hooks/usePreloadBrep";
import { useSpacebarPan } from "./hooks/useSpacebarPan";
import { useStableViewportFix } from "./hooks/useStableViewportFix";
import { useSyncDeviceProfile } from "./hooks/useSyncDeviceProfile";
import { useUpdateShown } from "./hooks/useUpdateShown";
import { useUpdateTransparentSelectionBase } from "./hooks/useUpdateTransparentSelectionBase";
import { Images } from "./images";
import { Markers } from "./markers";
import { selectDebugStats, selectLoadingHandles, selectSceneStatus } from "./renderSlice";
import { SceneError } from "./sceneError";
import { SelectBox } from "./selectBox";
import { Stamp } from "./stamp";

glMatrix.setMatrixArrayType(Array);

const DevRenderBenchmarkController = import.meta.env.DEV
    ? lazy(() =>
          import("../../dev/renderBenchmarkController").then(({ RenderBenchmarkController }) => ({
              default: RenderBenchmarkController,
          })),
      )
    : undefined;

const DevModelMarkCapture = import.meta.env.DEV
    ? lazy(() =>
          import("../../dev/modelMarkCapture").then(({ ModelMarkCapture }) => ({
              default: ModelMarkCapture,
          })),
      )
    : undefined;

/** Dev-only benchmark and click-mark tools. Omitted from production bundles. */
function DevRenderTools() {
    if (!import.meta.env.DEV || !DevRenderBenchmarkController || !DevModelMarkCapture) {
        return null;
    }
    const params = new URLSearchParams(window.location.search);
    const benchmark = params.get("renderBenchmark");
    const showBenchmark = benchmark === "1" || benchmark === "true";
    const showMarks = params.get("markModel") === "1";
    if (!showBenchmark && !showMarks) {
        return null;
    }
    return (
        <Suspense fallback={null}>
            {showBenchmark ? <DevRenderBenchmarkController /> : null}
            {showMarks ? <DevModelMarkCapture /> : null}
        </Suspense>
    );
}

const Canvas = styled("canvas")(
    () => css`
        outline: 0;
        touch-action: none;
        -webkit-touch-callout: none;
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 0;
    `,
);

const Svg = styled("svg")(
    () => css`
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        pointer-events: none;
        g {
            will-change: transform;
        }
    `,
);

export function Render3D({ uxFlavor }: { uxFlavor?: UxFlavor }) {
    const {
        state: { view, canvas, size },
        dispatch: dispatchGlobals,
    } = useExplorerGlobals();

    const loadingHandles = useAppSelector(selectLoadingHandles);
    const sceneStatus = useAppSelector(selectSceneStatus);
    const debugStats = useAppSelector(selectDebugStats);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);

    const [svg, setSvg] = useState<null | SVGSVGElement>(null);
    const [htmlInteractionContainer, setHtmlInteractionContainer] = useState<null | { update: () => void }>(null);
    const [markersContainer, setMarkersContainer] = useState<null | { update: () => void }>(null);

    const engine2dRenderFnRef = useRef<((moved: boolean, idleFrame?: boolean) => void) | undefined>();
    const pointerPosRef = useRef([0, 0] as [x: number, y: number]);
    const pointerDownStateRef = useRef<{
        timestamp: number;
        x: number;
        y: number;
    }>();
    const canvasRef: RefCallback<HTMLCanvasElement> = useCallback(
        (el) => {
            dispatchGlobals(explorerGlobalsActions.update({ canvas: el }));
        },
        [dispatchGlobals],
    );

    useEffect(() => {
        htmlInteractionContainer?.update();
    }, [size, htmlInteractionContainer]);

    useEffect(() => {
        markersContainer?.update();
    }, [size, markersContainer]);

    const cursor = useHandleCanvasCursor();
    const redirectWheelToCanvas = useRedirectWheelEvents();
    const eventHandlers = useCanvasEventHandlers({
        pointerPosRef,
        cursor,
        svg,
        pointerDownStateRef,
        engine2dRenderFnRef,
    });
    const onClick = useCanvasClickHandler({
        pointerDownStateRef,
        lastSelectBoxCompletionTime: eventHandlers.lastSelectBoxCompletionTime,
    });

    return (
        <Box
            id={ELEMENT_IDS.renderContainer}
            position="relative"
            width="100%"
            height="100%"
            sx={{ userSelect: "none", overflow: "hidden" }}
        >
            <DevRenderTools />
            <Hooks
                uxFlavor={uxFlavor}
                engine2dRenderFnRef={engine2dRenderFnRef}
                htmlInteractionContainer={htmlInteractionContainer}
                markersContainer={markersContainer}
            />
            <CameraStateDependentHooks />
            {isProcoreDesign ? <ProcoreOnlyHooks svg={svg} /> : <StandaloneOnlyHooks />}

            {loadingHandles.length !== 0 && (
                <Box position={"absolute"} top={0} width={1} display={"flex"} justifyContent={"center"}>
                    <LinearProgress />
                </Box>
            )}
            <Canvas id={ELEMENT_IDS.mainCanvas} onClick={onClick} {...eventHandlers} tabIndex={1} ref={canvasRef} />
            {sceneStatus.status === AsyncStatus.Error && <SceneError />}
            {[AsyncStatus.Initial, AsyncStatus.Loading].includes(sceneStatus.status) && <Loading />}
            {sceneStatus.status === AsyncStatus.Success && view && canvas && (
                <>
                    <RenderPerfSampler />
                    {debugStats.enabled && <PerformanceStats />}
                    <Engine2D
                        onMeasureUpdate={htmlInteractionContainer ? () => htmlInteractionContainer.update() : undefined}
                        pointerPosRef={pointerPosRef}
                        renderFnRef={engine2dRenderFnRef}
                        svg={svg}
                        onWheelCapture={redirectWheelToCanvas}
                    />
                    <Stamp />
                    <SelectBox />
                    <Svg width={size.width} height={size.height} ref={setSvg}>
                        <Markers ref={setMarkersContainer} />
                        <Engine2DInteractions />
                        <g id="cursor" />
                        <g id="camera-cursor" />
                    </Svg>
                    <div style={{ width: size.width, height: size.height }}>
                        <Engine2DHtmlInteractions ref={setHtmlInteractionContainer} />
                    </div>
                    <Images />
                </>
            )}
        </Box>
    );
}

/**
 * Sink component for side-effect-only hooks.
 *
 * Many of these hooks subscribe to the Redux store and would otherwise rerender
 * `Render3D` (and diff its entire canvas subtree) on every relevant state change.
 * Isolating them here keeps those rerenders confined to a component that renders
 * nothing.
 */
function Hooks({
    uxFlavor,
    engine2dRenderFnRef,
    htmlInteractionContainer,
    markersContainer,
}: {
    uxFlavor?: UxFlavor;
    engine2dRenderFnRef: MutableRefObject<((moved: boolean, idleFrame?: boolean) => void) | undefined>;
    htmlInteractionContainer: null | { update: () => void };
    markersContainer: null | { update: () => void };
}) {
    useHandleInit({ uxFlavor });
    useCrashTelemetry();
    // Session-scoped subscription: while the viewer is mounted this keeps ≥1
    // subscriber so the revisions stay cached (shared by all consumers). No-op
    // outside the Procore scope.
    useModelRevisions();
    useSyncDeviceProfile();
    useRefreshProjectPermissions();
    useHandleCameraMoved({ engine2dRenderFnRef, containers: [htmlInteractionContainer, markersContainer] });
    useHandleCameraState();
    useHandleCameraSpeed();
    usePreloadBrep();
    useHandleGrid();
    useHandleBackground();
    useHandleHighlights();
    useNotifyVisibilityChanged();
    useAutofreeze();
    useHandlePointVisualization();
    useHandleSubtrees();
    useHandleTerrain();
    useHandleAdvancedSettings();
    useHandleHeadlight();
    useHandleOffline();
    useHandleOutlineLasers();
    const spaceMouseEnabled = useSpaceMouse();
    useGlobalShortcuts();
    useStableViewportFix(uxFlavor);

    return spaceMouseEnabled ? <SpaceMouseCommands /> : null;
}

// Hooks that are dependent on the camera state and need to be re-run when the camera state changes.
// They run very often so separate them from the rest of the hooks to avoid re-rendering all the hooks on every frame.
function CameraStateDependentHooks() {
    useHandleClipping();

    return null;
}

/**
 * Sink component for side-effect-only hooks that are only needed in Procore mode.
 */
function ProcoreOnlyHooks({ svg }: { svg: SVGSVGElement | null }) {
    useSpacebarPan({ svg });
    useUpdateTransparentSelectionBase();

    return null;
}

/**
 * Sink component for side-effect-only hooks that are only needed in standalone mode.
 */
function StandaloneOnlyHooks() {
    useUpdateShown();
    useHandleInitialBookmark();
    useHandleUrlSearch();
    useHandleManhole();
    useHandleLocationMarker();
    useHandleCrossSection();
    useHandleDeviations();
    useHandleImages();
    useHandleClippingOutlines();

    useHandleJiraKeepAlive();
    useHandleXsiteManageKeepAlive();
    useHandleXsiteManageMachineLocations();
    useHandleDitioAuth();

    // This is used by at least Deviations and Horizontal alignment and is installed on app start
    // There's AsyncState based request deduplication, but if multiple instances of this
    // hook execute in the same render frame (before state is updated in rtk store) -
    // they all use Initial state and all fire requests
    useLoadAlignments();

    useRenderLocationFormAssets();

    useIntercom();

    return null;
}
